import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type {
  CompanyDocument,
  Question,
  QuestionCategory,
  Submission,
} from '../lib/types.ts';

type Tab = 'standard' | 'uploads';

export function MyDocuments() {
  const [tab, setTab] = useState<Tab>('standard');

  const documentsQuery = useQuery({
    queryKey: ['me', 'documents'],
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>('/api/me/documents'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['me', 'submissions'],
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const questionsQuery = useQuery({
    queryKey: ['me', 'questions'],
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['me', 'question-categories'],
    queryFn: () =>
      companyApi.get<{ categories: QuestionCategory[] }>('/api/me/question-categories'),
  });

  const documents = documentsQuery.data?.documents ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const categories = categoriesQuery.data?.categories ?? [];

  const adoptedIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of submissions) {
      for (const a of s.submission_adoptions) {
        if (a.mode === 'adopted') set.add(a.document_id);
      }
    }
    return set;
  }, [submissions]);

  if (
    documentsQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Documents</h1>
        <p className="mt-1 text-sm text-slate-500">
          Standard policies you can adopt and the documents you have uploaded.
        </p>
      </div>

      <div
        className="flex gap-1 border-b"
        style={{ borderColor: '#E2E4EA' }}
        role="tablist"
        aria-label="Documents view"
      >
        <TabButton active={tab === 'standard'} onClick={() => setTab('standard')}>
          Standard policies
        </TabButton>
        <TabButton active={tab === 'uploads'} onClick={() => setTab('uploads')}>
          My uploads
        </TabButton>
      </div>

      {tab === 'standard' ? (
        <StandardPolicies documents={documents} adoptedIds={adoptedIds} />
      ) : (
        <MyUploads
          submissions={submissions}
          questions={questions}
          categories={categories}
          documents={documents}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm transition ${
        active ? 'font-semibold' : 'border-transparent font-medium hover:text-slate-700'
      }`}
      style={
        active
          ? { borderColor: 'var(--brand-accent)', color: 'var(--brand-primary)' }
          : { color: '#44587A' }
      }
    >
      {children}
    </button>
  );
}

function StandardPolicies({
  documents,
  adoptedIds,
}: {
  documents: CompanyDocument[];
  adoptedIds: Set<string>;
}) {
  const grouped = new Map<string, CompanyDocument[]>();
  for (const d of documents) {
    const list = grouped.get(d.category) ?? [];
    list.push(d);
    grouped.set(d.category, list);
  }

  async function download(id: string) {
    const res = await companyApi
      .get<{ url: string }>(`/api/me/documents/${id}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  if (documents.length === 0) {
    return (
      <EmptyState
        title="No documents available"
        description="Standard policies will appear here once published."
      />
    );
  }

  return (
    <div className="space-y-6">
      {[...grouped.entries()].map(([category, docs]) => (
        <section key={category}>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {category}
          </h2>
          <div className="space-y-2">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4"
              >
                <div>
                  <div className="text-sm font-medium text-slate-900">{d.name}</div>
                  <div className="text-xs text-slate-500">v{d.version}</div>
                </div>
                <div className="flex items-center gap-3">
                  {adoptedIds.has(d.id) && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                      Adopted
                    </span>
                  )}
                  <button
                    onClick={() => void download(d.id)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

type UploadRow =
  | {
      kind: 'file';
      key: string;
      file_name: string;
      created_at: string;
      questionId: string;
      questionLabel: string;
      optionKey: string | null;
      fileId: string;
    }
  | {
      kind: 'adoption';
      key: string;
      file_name: string;
      created_at: string;
      questionId: string;
      questionLabel: string;
      optionKey: string | null;
      documentId: string;
      documentVersion: string | null;
    };

function MyUploads({
  submissions,
  questions,
  categories,
  documents,
}: {
  submissions: Submission[];
  questions: Question[];
  categories: QuestionCategory[];
  documents: CompanyDocument[];
}) {
  const questionById = useMemo(
    () => new Map(questions.map((q) => [q.id, q])),
    [questions],
  );
  const docById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);

  // Build per-category lists of uploaded files + adopted standard documents.
  const rowsByCategory = useMemo(() => {
    const map = new Map<string, UploadRow[]>();
    for (const cat of categories) map.set(cat.id, []);

    for (const s of submissions) {
      const q = questionById.get(s.question_id);
      if (!q) continue;
      const bucket = map.get(q.category_id) ?? [];

      for (const f of s.submission_files) {
        bucket.push({
          kind: 'file',
          key: `file:${f.id}`,
          file_name: f.file_name,
          created_at: f.created_at,
          questionId: q.id,
          questionLabel: q.label,
          optionKey: f.option_key,
          fileId: f.id,
        });
      }

      for (const a of s.submission_adoptions) {
        if (a.mode !== 'adopted') continue;
        const doc = docById.get(a.document_id);
        if (!doc) continue;
        bucket.push({
          kind: 'adoption',
          key: `adoption:${a.id}`,
          file_name: doc.name,
          created_at: a.acknowledged_at ?? a.created_at,
          questionId: q.id,
          questionLabel: q.label,
          optionKey: a.option_key,
          documentId: doc.id,
          documentVersion: doc.version ?? null,
        });
      }

      map.set(q.category_id, bucket);
    }

    for (const [k, list] of map) {
      list.sort((a, b) => a.created_at.localeCompare(b.created_at));
      map.set(k, list);
    }
    return map;
  }, [submissions, questionById, docById, categories]);

  const totalRows = useMemo(
    () => [...rowsByCategory.values()].reduce((n, list) => n + list.length, 0),
    [rowsByCategory],
  );

  async function downloadFile(questionId: string, fileId: string) {
    const res = await companyApi
      .get<{ url: string }>(
        `/api/me/submissions/${questionId}/files/${fileId}/download-url`,
      )
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function downloadStandardDoc(documentId: string) {
    const res = await companyApi
      .get<{ url: string }>(`/api/me/documents/${documentId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  if (totalRows === 0) {
    return (
      <EmptyState
        title="No uploads yet"
        description="Documents you upload and policies you adopt as part of your questionnaire will appear here, grouped by category."
      />
    );
  }

  return (
    <div className="space-y-6">
      {categories.map((cat) => {
        const list = rowsByCategory.get(cat.id) ?? [];
        if (list.length === 0) return null;
        return (
          <section key={cat.id}>
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {cat.name}
              </h2>
              <span className="text-xs text-slate-500">
                {list.length} item{list.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="space-y-2">
              {list.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900">
                      {row.file_name}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">
                      {row.questionLabel}
                      {row.optionKey ? ` · ${row.optionKey}` : ''}
                      {row.kind === 'adoption' && row.documentVersion
                        ? ` · v${row.documentVersion}`
                        : ''}
                    </div>
                  </div>
                  <div className="ml-3 flex items-center gap-3 shrink-0">
                    {row.kind === 'adoption' && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        Adopted
                      </span>
                    )}
                    <button
                      onClick={() =>
                        row.kind === 'file'
                          ? void downloadFile(row.questionId, row.fileId)
                          : void downloadStandardDoc(row.documentId)
                      }
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
